import React, { createContext, useContext, useMemo, ReactNode } from "react";
import type { ApolloError } from "@apollo/client";
import {
  useIntegratedDataSources,
  useDataSourceDefinition,
  useThreatIntelligenceDefinition,
  useIntegratedDataSourcesFastInfo,
  useThreatIntelligenceDataSourcesFastInfo,
  useDatasourcesInstances,
  type DefinitionNode,
  type DefinitionEdge,
  type DataSourceDefinitionsResponse,
  type DatasourceEdge,
  type IntegratedDataSourcesResponse,
  type DatasourceInstanceEdge,
  type DataSourcesFastInfoResponse,
} from "../AppSettings/Integrations/DataSources";

// Re-export types for consumers of the provider
export type {
  DefinitionNode,
  DefinitionEdge,
  DataSourceDefinitionsResponse,
  DatasourceEdge,
  IntegratedDataSourcesResponse,
  DatasourceInstanceNode,
  DatasourceInstanceEdge,
  DataSourcesFastInfoResponse,
} from "../AppSettings/Integrations/DataSources";

export interface DatasourcesContextType {
  integratedDataSources: IntegratedDataSourcesResponse | undefined;
  loading: boolean;
  error?: ApolloError;
  threatIntelligenceDefinitions: DefinitionNode[];
  threatIntelligenceError?: ApolloError;
  threatIntelligenceLoading: boolean;
  integratedThreatIntelligenceResponse:
    | DataSourceDefinitionsResponse
    | undefined;
  dataSourceDefinitionsDataRegular: DataSourceDefinitionsResponse | undefined;
  dataSourceDefinitionsRegularLoading: boolean;
  dataSourceDefinitionsRegularError?: ApolloError;
  dataSourcesFastInfoRegular: DataSourcesFastInfoResponse | undefined;
  dataSourcesFastInfoThreatIntelligence:
    | DataSourcesFastInfoResponse
    | undefined;
  dataSourcesFastInfoThreatIntelligenceError?: ApolloError;
  dataSourcesFastInfoThreatIntelligenceLoading: boolean;
  dataSourcesFastInfoLoading: boolean;
  dataSourcesFastInfoError?: ApolloError;
  datasourcesInstancesEdges: DatasourceInstanceEdge[];
  datasourcesInstancesLoading: boolean;
  datasourcesInstancesError?: ApolloError;
  refetchDatasourcesInstances: () => void;
}

const initialContextValue: DatasourcesContextType = {
  integratedDataSources: undefined,
  loading: true,
  error: undefined,
  threatIntelligenceDefinitions: [],
  threatIntelligenceError: undefined,
  threatIntelligenceLoading: true,
  integratedThreatIntelligenceResponse: undefined,
  dataSourceDefinitionsDataRegular: undefined,
  dataSourceDefinitionsRegularLoading: true,
  dataSourceDefinitionsRegularError: undefined,
  dataSourcesFastInfoRegular: undefined,
  dataSourcesFastInfoThreatIntelligence: undefined,
  dataSourcesFastInfoThreatIntelligenceError: undefined,
  dataSourcesFastInfoThreatIntelligenceLoading: true,
  dataSourcesFastInfoLoading: true,
  dataSourcesFastInfoError: undefined,
  datasourcesInstancesEdges: [],
  datasourcesInstancesLoading: true,
  datasourcesInstancesError: undefined,
  refetchDatasourcesInstances: () => {},
};

const DatasourcesContext =
  createContext<DatasourcesContextType>(initialContextValue);

interface DatasourcesProviderProps {
  children: ReactNode;
}

type DefinitionMapValue = DefinitionNode & { isSeemplicity?: boolean };

const getDefinitionData = (
  definition: DefinitionNode,
  definitionsMap: Map<string, DefinitionMapValue>
) => {
  const decodedId = atob(definition.id)?.split(":")?.[1];
  if (!decodedId || definitionsMap.has(decodedId)) {
    return;
  }
  definitionsMap.set(decodedId, {
    ...definition,
    id: decodedId,
    isSeemplicity: definition.category === "Public Threat Intelligence",
  });
};

// Provider component
export const DatasourcesProvider: React.FC<DatasourcesProviderProps> = ({
  children,
}) => {
  const { integratedDataSources, loading, integratedDataSourcesError } =
    useIntegratedDataSources();

  const {
    threatIntelligenceDefinitions: integratedThreatIntelligenceResponse,
    threatIntelligenceDefinitionsError: threatIntelligenceError,
    threatIntelligenceDefinitionsLoading: threatIntelligenceLoading,
  } = useThreatIntelligenceDefinition({});

  const {
    integratedDataSources: dataSourceDefinitionsDataRegular,
    loading: dataSourceDefinitionsRegularLoading,
    integratedDataSourcesError: dataSourceDefinitionsRegularError,
  } = useDataSourceDefinition({});

  const {
    integratedDataSourcesFastInfo: dataSourcesFastInfoRegular,
    integratedDataSourcesFastInfoError: dataSourcesFastInfoError,
    integratedDataSourcesFastInfoLoading: dataSourcesFastInfoLoading,
  } = useIntegratedDataSourcesFastInfo(true);

  const {
    integratedDataSourcesFastInfo: dataSourcesFastInfoThreatIntelligence,
    integratedDataSourcesFastInfoError:
      dataSourcesFastInfoThreatIntelligenceError,
    integratedDataSourcesFastInfoLoading:
      dataSourcesFastInfoThreatIntelligenceLoading,
  } = useThreatIntelligenceDataSourcesFastInfo(true);

  const {
    datasourcesInstancesEdges,
    datasourcesInstancesLoading,
    datasourcesInstancesError,
    refetchDatasourcesInstances,
  } = useDatasourcesInstances({
    queryOptions: { fetchPolicy: "cache-first" },
    onlyVisible: true, // hidden datasources (ACTIVE_HIDDEN, DELETED) must never be shown to users
  });

  // Extract the actual definitions from the threat intelligence response
  const threatIntelligenceDefinitions = useMemo((): DefinitionNode[] => {
    return (
      integratedThreatIntelligenceResponse?.datasource_definitions?.edges?.map(
        (edge: DefinitionEdge) => edge.node
      ) ?? []
    );
  }, [integratedThreatIntelligenceResponse]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    (): DatasourcesContextType => ({
      integratedDataSources,
      loading,
      error: integratedDataSourcesError,
      threatIntelligenceDefinitions,
      threatIntelligenceError,
      threatIntelligenceLoading,
      integratedThreatIntelligenceResponse,
      dataSourceDefinitionsDataRegular,
      dataSourceDefinitionsRegularLoading,
      dataSourceDefinitionsRegularError,
      dataSourcesFastInfoRegular,
      dataSourcesFastInfoThreatIntelligence,
      dataSourcesFastInfoThreatIntelligenceError,
      dataSourcesFastInfoThreatIntelligenceLoading,
      dataSourcesFastInfoLoading,
      dataSourcesFastInfoError,
      datasourcesInstancesEdges: datasourcesInstancesEdges ?? [],
      datasourcesInstancesLoading,
      datasourcesInstancesError,
      refetchDatasourcesInstances,
    }),
    [
      integratedDataSources,
      loading,
      integratedDataSourcesError,
      threatIntelligenceDefinitions,
      threatIntelligenceError,
      threatIntelligenceLoading,
      integratedThreatIntelligenceResponse,
      dataSourceDefinitionsDataRegular,
      dataSourceDefinitionsRegularLoading,
      dataSourceDefinitionsRegularError,
      dataSourcesFastInfoRegular,
      dataSourcesFastInfoThreatIntelligence,
      dataSourcesFastInfoThreatIntelligenceError,
      dataSourcesFastInfoThreatIntelligenceLoading,
      dataSourcesFastInfoLoading,
      dataSourcesFastInfoError,
      datasourcesInstancesEdges,
      datasourcesInstancesLoading,
      datasourcesInstancesError,
      refetchDatasourcesInstances,
    ]
  );

  return (
    <DatasourcesContext.Provider value={contextValue}>
      {children}
    </DatasourcesContext.Provider>
  );
};

// Custom hook to use the context
export const useDatasources = (): DatasourcesContextType => {
  return useContext(DatasourcesContext);
};

// Helper hooks for common use cases
export const useDatasourcesList = (): DatasourceEdge[] => {
  const { integratedDataSources } = useDatasources();
  return integratedDataSources?.datasources?.edges ?? [];
};

export const useDatasourceDefinitions = (): DefinitionMapValue[] => {
  const { integratedDataSources } = useDatasources();

  return useMemo(() => {
    const datasourcesList = integratedDataSources?.datasources?.edges ?? [];
    const definitionsMap = new Map<string, DefinitionMapValue>();

    datasourcesList.forEach((datasource) => {
      const definition = datasource?.node?.datasource_definition;
      if (definition?.id) {
        getDefinitionData(definition as DefinitionNode, definitionsMap);
      }
    });

    return Array.from(definitionsMap.values());
  }, [integratedDataSources]);
};

export const useFailedDatasources = (): DatasourceEdge[] => {
  const { integratedDataSources } = useDatasources();
  return (
    integratedDataSources?.datasources?.edges?.filter(
      (ds) => ds.node?.connectionStatus === "FAILED"
    ) ?? []
  );
};

export const useHasFailedDatasources = () => {
  const failedDatasources = useFailedDatasources();
  return failedDatasources.length > 0;
};

export const useThreatIntelligenceDefinitions = (): DefinitionMapValue[] => {
  const { threatIntelligenceDefinitions } = useDatasources();

  return useMemo(() => {
    const definitionsMap = new Map<string, DefinitionMapValue>();

    threatIntelligenceDefinitions.forEach((definition) => {
      const def = definition as DefinitionNode;
      if (def?.id) {
        getDefinitionData(def, definitionsMap);
      }
    });

    return Array.from(definitionsMap.values());
  }, [threatIntelligenceDefinitions]);
};

export const useDatasourcesAndThreatIntelligenceDefinitions = () => {
  const datasourcesDefinitions = useDatasourceDefinitions();
  const threatIntelligenceDefinitions = useThreatIntelligenceDefinitions();
  const result = useMemo(() => {
    return [...datasourcesDefinitions, ...threatIntelligenceDefinitions];
  }, [datasourcesDefinitions, threatIntelligenceDefinitions]);

  return result;
};

export default DatasourcesProvider;
